import { fetchRestData } from '@/services/ApiRest';
import { AGENT_API_URL } from '@/services/agent/config';
import rem from '@/utils/rem';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Code,
  Divider,
  Flex,
  HStack,
  Heading,
  Input,
  ListItem,
  OrderedList,
  Spinner,
  Text,
  UnorderedList,
  VStack,
  useClipboard,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface TelegramStatus {
  linked: boolean;
  telegram_chat_id: number | null;
  pending_code: string | null;
}

interface LinkCodeResponse {
  code: string;
  bot_username: string;
}

const telegramKeys = {
  status: ['agent', 'telegram', 'status'] as const,
};

function TelegramSettings() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('gray.50', 'gray.700');

  const [linkCode, setLinkCode] = useState<LinkCodeResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: telegramKeys.status,
    queryFn: () => fetchRestData<TelegramStatus>(`${AGENT_API_URL}/telegram/status`),
    staleTime: 1000 * 30,
  });

  const { onCopy: onCopyCode, hasCopied: hasCopiedCode } = useClipboard(linkCode?.code || '');
  const { onCopy: onCopyBotLink, hasCopied: hasCopiedBotLink } = useClipboard(
    linkCode ? `https://t.me/${linkCode.bot_username}?start=${linkCode.code}` : '',
  );

  const handleGenerateCode = useCallback(async () => {
    setIsGenerating(true);
    try {
      const result = await fetchRestData<LinkCodeResponse>(`${AGENT_API_URL}/telegram/link`, {
        method: 'POST',
      });
      if (result) {
        setLinkCode(result);
        toast({
          title: 'Link code generated',
          description: 'Use this code in Telegram within 15 minutes.',
          status: 'success',
          duration: 5000,
        });
      }
    } catch {
      toast({ title: 'Failed to generate code', status: 'error', duration: 3000 });
    } finally {
      setIsGenerating(false);
    }
  }, [toast]);

  const handleDisconnect = useCallback(async () => {
    setIsDisconnecting(true);
    try {
      await fetchRestData(`${AGENT_API_URL}/telegram/disconnect`, { method: 'POST' });
      queryClient.invalidateQueries({ queryKey: telegramKeys.status });
      setLinkCode(null);
      toast({ title: 'Telegram disconnected', status: 'info', duration: 3000 });
    } catch {
      toast({ title: 'Failed to disconnect', status: 'error', duration: 3000 });
    } finally {
      setIsDisconnecting(false);
    }
  }, [queryClient, toast]);

  const handlePollStatus = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: telegramKeys.status });
  }, [queryClient]);

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" height="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const isLinked = status?.linked ?? false;

  return (
    <Box position="relative" overflowY="auto" width="100%" height="100vh">
      <Box maxWidth="800px" margin="0 auto" padding={rem(32)} paddingTop={rem(48)} paddingBottom={rem(96)}>
        <Button
          marginBottom={4}
          colorScheme="gray"
          leftIcon={<span>&#8592;</span>}
          onClick={() => navigate(-1)}
          size="sm"
          variant="outline"
        >
          Back
        </Button>

        <Flex alignItems="center" gap={3} marginBottom={rem(32)}>
          <Text fontSize="3xl">&#9992;&#65039;</Text>
          <Box>
            <Heading as="h1" size="xl">
              Connect Telegram
            </Heading>
            <Text color="gray.500" fontSize="sm">
              Chat with RPM AI from your phone via Telegram
            </Text>
          </Box>
          {isLinked && (
            <Badge colorScheme="green" fontSize="sm" ml="auto" px={3} py={1}>
              Connected
            </Badge>
          )}
        </Flex>

        {/* Connection Status */}
        {isLinked ? (
          <Card bg={bgColor} marginBottom={6} borderWidth="1px" borderColor="green.500">
            <CardBody>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Text fontSize="lg" fontWeight="bold" color="green.400">
                    Telegram is connected
                  </Text>
                </HStack>
                <Text color="gray.400">
                  Bot: <Code>@RPMPlannerBot</Code> &middot; Chat ID: <Code>{status?.telegram_chat_id}</Code>
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Send messages to @RPMPlannerBot on Telegram and they'll be processed by RPM AI with full access to
                  your categories, actions, blocks, and projects.
                </Text>
                <Divider />
                <HStack spacing={3}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    onClick={handleDisconnect}
                    isLoading={isDisconnecting}
                  >
                    Disconnect
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        ) : (
          /* Link Flow */
          <Card bg={bgColor} marginBottom={6}>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                {linkCode ? (
                  /* Show generated code */
                  <>
                    <Box bg="blue.900" borderRadius="md" p={4}>
                      <Text fontWeight="bold" color="blue.200" mb={2}>
                        Step 1: Open this link in Telegram
                      </Text>
                      <Flex gap={2}>
                        <Input
                          value={`https://t.me/${linkCode.bot_username}?start=${linkCode.code}`}
                          isReadOnly
                          size="sm"
                          fontFamily="mono"
                          fontSize="xs"
                          bg="whiteAlpha.100"
                        />
                        <Button size="sm" colorScheme="blue" onClick={onCopyBotLink} minW="70px">
                          {hasCopiedBotLink ? 'Copied!' : 'Copy'}
                        </Button>
                      </Flex>
                    </Box>

                    <Box bg={cardBg} borderRadius="md" p={4}>
                      <Text fontWeight="bold" mb={2}>
                        Or manually:
                      </Text>
                      <OrderedList spacing={2} fontSize="sm">
                        <ListItem>
                          Open Telegram and search for <Code>@{linkCode.bot_username}</Code>
                        </ListItem>
                        <ListItem>
                          Send this message: <Code>/start {linkCode.code}</Code>{' '}
                          <Button size="xs" variant="ghost" onClick={onCopyCode}>
                            {hasCopiedCode ? 'Copied!' : 'Copy code'}
                          </Button>
                        </ListItem>
                        <ListItem>Wait for confirmation, then click "Check Status" below</ListItem>
                      </OrderedList>
                    </Box>

                    <Text fontSize="xs" color="gray.500">
                      This code expires in 15 minutes.
                    </Text>

                    <HStack>
                      <Button size="sm" colorScheme="blue" onClick={handlePollStatus}>
                        Check Status
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleGenerateCode} isLoading={isGenerating}>
                        Generate New Code
                      </Button>
                    </HStack>
                  </>
                ) : (
                  /* Initial state — generate code */
                  <>
                    <Text>
                      Connect your Telegram account to chat with RPM AI on the go. Create actions, check your schedule,
                      and manage your plans — all from Telegram.
                    </Text>
                    <Box bg={cardBg} borderRadius="md" p={4}>
                      <Text fontSize="sm" fontWeight="bold" mb={2}>
                        How to connect:
                      </Text>
                      <OrderedList spacing={2} fontSize="sm" color="gray.300">
                        <ListItem>Click "Generate Link Code" below</ListItem>
                        <ListItem>
                          A link will appear — tap it to open <strong>@RPMPlannerBot</strong> in Telegram
                        </ListItem>
                        <ListItem>The bot will confirm your account is linked</ListItem>
                        <ListItem>Start chatting — just type naturally like you would in the app</ListItem>
                      </OrderedList>
                    </Box>
                    <Button colorScheme="blue" size="lg" onClick={handleGenerateCode} isLoading={isGenerating}>
                      Generate Link Code
                    </Button>
                  </>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* What you can do */}
        <Card bg={bgColor} marginBottom={6}>
          <CardBody>
            <Heading size="md" mb={3}>
              What you can do via Telegram
            </Heading>
            <UnorderedList spacing={2} fontSize="sm" color="gray.300">
              <ListItem>
                <strong>"today"</strong> — See all your actions for today
              </ListItem>
              <ListItem>
                <strong>"week"</strong> — Get your weekly summary
              </ListItem>
              <ListItem>
                <strong>"add Buy groceries tomorrow Personal"</strong> — Create actions with natural language
              </ListItem>
              <ListItem>
                <strong>"done Call investor"</strong> — Complete actions by name
              </ListItem>
              <ListItem>
                <strong>"Plan my week"</strong> — Get RPM-guided weekly planning
              </ListItem>
              <ListItem>
                <strong>"Show my categories"</strong> — List all your life categories
              </ListItem>
            </UnorderedList>
          </CardBody>
        </Card>

        {/* How it works */}
        <Card bg={bgColor}>
          <CardBody>
            <Heading size="md" mb={3}>
              How it works
            </Heading>
            <OrderedList spacing={2} fontSize="sm" color="gray.300">
              <ListItem>
                Click <strong>"Generate Link Code"</strong> above
              </ListItem>
              <ListItem>Open the link in Telegram (or search for the bot manually)</ListItem>
              <ListItem>The bot confirms your account is connected</ListItem>
              <ListItem>Start chatting — RPM AI has full access to your planning data</ListItem>
            </OrderedList>
            <Text fontSize="xs" color="gray.500" mt={3}>
              Your Telegram messages are processed by the same RPM AI that powers the in-app chat. All data stays within
              your account.
            </Text>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
}

export default TelegramSettings;
